import JSON5 from 'json5';
import * as React from 'react';

import { RichTextView } from '@/components/RichTextEditor/RichTextView';
import ToolIcon from '@/icons/ToolIcon.svg?react';
import { getMessageType } from '@/utils/messages';

import { RenderModeContext } from '../../../control/state';
import {
  foreignDataToTree,
  parseForeignData,
} from '../../../data/foreign-data';
import { JSONValue } from '../../../data/json';
import { Message } from '../../../data/message';
import { ArgsTable } from '../common/args-table';
import {
  ForeginDataTreeContext,
  ForeignDataRecursiveProps,
  ForeignDataTreeView,
} from '../common/foreign-data-tree';
import { JsonView } from '../common/json-view';

function tryParseJSONPretty(
  str: string
): { ok: false } | { ok: true; value: JSONValue } {
  try {
    const value = JSON.parse(str, (_, value) => {
      if (typeof value === 'string') {
        try {
          return JSON5.parse(value);
        } catch {
          return value;
        }
      }
      return value;
    });

    return { ok: true, value };
  } catch {
    return { ok: false };
  }
}

export const MessageViewInner = (
  props: {
    message: Message;
    title?: string | undefined;
  } & ForeignDataRecursiveProps
) => {
  const { renderPrettyOptions } = React.useContext(RenderModeContext);
  const enableMarkdown = renderPrettyOptions.allow.includes('markdown');
  const messageType = getMessageType(props.message);

  if ((props.message.tool_calls?.length ?? -1) > 0) {
    return (
      <div className="flex flex-col gap-2 overflow-auto whitespace-pre-wrap [scrollbar-width:thin]">
        <span className="text-xs font-semibold uppercase text-tertiary">
          {messageType}
        </span>
        <RichTextView
          content={props.message.content}
          enableMarkdown={enableMarkdown}
        />

        {props.message.tool_calls?.map((call, i) => (
          <div key={i} className="flex flex-col gap-3">
            <ArgsTable
              id={call.id}
              label={call.name}
              args={Object.entries(call.args ?? {})}
            />
          </div>
        ))}
      </div>
    );
  }

  if (props.message.type === 'tool') {
    // this should not happen, but at least render the contents somehow
    if (typeof props.message.content !== 'string') {
      return (
        <JsonView
          json={{
            ...(props.message.name && { name: props.message.name }),
            content: props.message.content,
          }}
        />
      );
    }

    const json = tryParseJSONPretty(props.message.content);
    return (
      <div className="flex flex-col gap-2">
        {props.message.name && (
          <div className="inline-flex min-w-0 flex-shrink items-center gap-2 self-start rounded-full border border-secondary px-2">
            <ToolIcon className="h-4 w-4" />
            <span className="truncate font-mono">{props.message.name}</span>
          </div>
        )}

        {json.ok ? (
          <ForeginDataTreeContext.Provider
            value={{
              variant: 'json-like',
              streaming: false,
              location: 'studio',
            }}
          >
            <ForeignDataTreeView
              data={foreignDataToTree({
                data: parseForeignData(json.value),
              })}
              {...props}
            />
          </ForeginDataTreeContext.Provider>
        ) : (
          <RichTextView
            content={props.message.content}
            enableMarkdown={enableMarkdown}
          />
        )}
      </div>
    );
  }

  let emptyText = 'Empty message';

  // handle RemoveMessage type
  if (props.message.type === 'remove') {
    emptyText = `Message ID '${props.message.id}' has been deleted`;
  }

  return (
    <div className="flex flex-col gap-2 overflow-auto whitespace-pre-wrap [scrollbar-width:thin]">
      <span className="text-xs font-semibold uppercase text-tertiary">
        {messageType}
      </span>
      <RichTextView
        content={props.message.content}
        empty={<div className="text-tertiary">{emptyText}</div>}
        enableMarkdown={enableMarkdown}
      />
    </div>
  );
};
